/**
 * Project layout state: the CONTENT tree, the CHROME around it, and which pane
 * has focus. Three things with three different lifetimes, deliberately.
 *
 * THE TREE LIVES IN THE URL. What is open is addressable — reload and your panes
 * come back, open a second window on a different set, send the link and the same
 * things open. There is no second copy in localStorage to disagree with it.
 *
 * CHROME LIVES IN localStorage. The files sidebar and settings are not panes: you
 * never want two and you never split one, so they are a flag and a width, and
 * they are a per-device preference rather than part of what you are working on.
 * Nobody wants a link to reopen someone else's sidebar width.
 *
 * FOCUS IS TRANSIENT. Which pane the caret is in is neither addressable nor a
 * preference — it is where you are right now, so it is plain state, and a reload
 * starts from the first pane.
 *
 * SO IS A MAXIMIZED PANE (RFC 0305). It is a way of looking at the space, not a
 * change to it: the tree, the url and the space document never hear about it,
 * which is what makes restoring exact.
 *
 * THE OTHER SPACES' TREES STAY MOUNTED. A space you are not in is not gone: its
 * tree comes off the spaces document and is rendered hidden, so a scroll
 * position, a pty and an iframe survive switching away and back. The same
 * pattern maximize uses one level down (RFC 0305 3b), applied to spaces. What
 * makes it work is that leaf ids are remembered PER SPACE: the ids a space had
 * while it was on screen are the ids its hidden tree adopts, and vice versa, so
 * React sees one pane crossing the switch rather than one closing and another
 * opening.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  type ArrangePreset,
  addTab,
  adoptIds,
  agentLeaf,
  arrange,
  type Content,
  type ContentLeaf,
  type ContentNode,
  closeLeaf,
  closeTab,
  type DropEdge,
  findLeaf,
  leafAt,
  leafId,
  leafShowing,
  leaves,
  moveLeaf,
  type NodePath,
  openCodeTab,
  openLeaf,
  paneTabs,
  pickerLeaf,
  resizeSplit,
  selectTab,
  setLeafContent,
} from '../../features/code/contentTree'
import { SPACE_PARAM } from '../spaces/spaceUrl'
import { CONTENT_PARAM, decodeContent, encodeContent } from './contentUrl'

const CHROME_KEY = 'mod_w_chrome_v1'

export interface Chrome {
  /** The files sidebar. */
  sidebar: boolean
  sidebarWidth: number
  /** The settings pane, chrome for the same reason: only ever one. */
  settings: boolean
  /**
   * Where the spaces list is drawn (RFC 0281 §7, S5a): down the side with the
   * rest of the sidebar, or along the top as tabs.
   *
   * CHROME, so it lives here with the sidebar width rather than
   * on the spaces document. It changes where the rows are drawn and nothing
   * about what a space IS, which is exactly what makes it a device preference
   * and not a shared fact — nobody wants a laptop's choice of tabs to move
   * someone else's sidebar.
   */
  spacesAsTabs: boolean
  /**
   * The compact composer kept out of the way (⌘J): shown only when the
   * pointer reaches the bottom edge, the way a floating sidebar is. A device
   * preference like the sidebar's, so it lives here with it.
   */
  composerHidden: boolean
}

const CHROME_DEFAULTS: Chrome = {
  // Collapsed until asked for (RFC 0337 S6). A new user's sidebar is Spaces,
  // Skills and a project of internal nouns; the first screen is the composer.
  // Anyone who has toggled it keeps their stored choice.
  sidebar: false,
  sidebarWidth: 240,
  settings: false,
  // Side by default: it costs no header room, which is the band RFC 0278 and
  // RFC 0280 are already competing for (§10).
  spacesAsTabs: false,
  composerHidden: false,
}

export const SIDEBAR_MIN = 180
export const SIDEBAR_MAX = 420

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v))

function loadChrome(): Chrome {
  try {
    const raw = localStorage.getItem(CHROME_KEY)
    if (!raw) return CHROME_DEFAULTS
    const parsed = JSON.parse(raw) as Partial<Chrome>
    return {
      ...CHROME_DEFAULTS,
      ...parsed,
      sidebarWidth: clamp(
        parsed.sidebarWidth ?? CHROME_DEFAULTS.sidebarWidth,
        SIDEBAR_MIN,
        SIDEBAR_MAX,
      ),
    }
  } catch {
    return CHROME_DEFAULTS
  }
}

/** What a fresh window opens on: the session list, and nothing else. */
const emptyTree = (): ContentNode => agentLeaf()

/** A space as the layout needs it: its id and its stored tree. */
export interface SpaceTreeSource {
  spaceId: string
  content?: string
}

/** A space's tree, decoded, for the shell to render hidden. */
export interface SpaceTree {
  spaceId: string
  tree: ContentNode
}

export function useLayout(opts?: {
  /** Every space in the World. The ones not on screen are rendered hidden. */
  spaces?: readonly SpaceTreeSource[]
  /** The space the window is in, resolved; null while that is being decided. */
  spaceId?: string | null
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const scope = searchParams.get(SPACE_PARAM) ?? ''
  /**
   * The key the on-screen tree's ids are remembered under. The resolved space
   * when there is one; the url's while resolution is pending, which is the
   * empty string on a fresh window.
   */
  const key = opts?.spaceId ?? scope

  // Decoded per render rather than mirrored into state: the url is the truth, and
  // a copy would need syncing on back/forward, which is the bug a single source
  // of truth avoids.
  //
  // The ids, though, must survive the round trip. They are not in the url, so a
  // decode mints new ones, and panes are keyed by id — meaning every pane would
  // remount on every change, taking the seam being dragged and every scroll
  // position with it. `adoptIds` keeps the ids where the tree is structurally
  // the same, so identity follows what a person would call "the same pane".
  //
  // REMEMBERED PER SPACE. One tree per space id, holding whichever tree that
  // space was last given: the url's while it is on screen, the document's
  // while it is hidden. A switch adopts the incoming space's ids from its own
  // hidden tree and never from the outgoing one, which is what keeps every
  // leaf id unique across the World's spaces (`leafId` is a counter, so fresh
  // ids never collide; only adoption could) and what lets a hidden tree keep
  // the DOM the active one built.
  const remembered = useRef(new Map<string, ContentNode>())
  const previousKey = useRef(key)
  const tree = useMemo<ContentNode>(() => {
    const decoded =
      decodeContent(searchParams.get(CONTENT_PARAM)) ?? emptyTree()
    // A window that has just resolved its space carries the tree it had while
    // unresolved across, so nothing remounts on the way in.
    const prior =
      remembered.current.get(key) ??
      (previousKey.current === '' ? (remembered.current.get('') ?? null) : null)
    previousKey.current = key
    const adopted = adoptIds(prior, decoded)
    remembered.current.set(key, adopted)
    return adopted
  }, [searchParams, key])

  /**
   * The trees of every space NOT on screen, decoded from the spaces document.
   *
   * Cached by content string, so a space nobody touched hands back the same
   * tree object and its panes do not re-render on every unrelated change to
   * the document. The cache is bypassed for a space that has just left the
   * screen: its remembered tree is the url's, whose ids the hidden tree must
   * adopt, and the cached one predates them.
   */
  const hiddenCache = useRef(
    new Map<string, { content: string; tree: ContentNode }>(),
  )
  const spaces = opts?.spaces
  const hiddenTrees = useMemo<SpaceTree[]>(() => {
    const out: SpaceTree[] = []
    for (const space of spaces ?? []) {
      if (space.spaceId === key) continue
      const content = space.content ?? ''
      const prior = remembered.current.get(space.spaceId) ?? null
      const cached = hiddenCache.current.get(space.spaceId)
      let hidden: ContentNode
      if (cached && cached.content === content && cached.tree === prior) {
        hidden = cached.tree
      } else {
        hidden = adoptIds(prior, decodeContent(content) ?? emptyTree())
        hiddenCache.current.set(space.spaceId, { content, tree: hidden })
      }
      remembered.current.set(space.spaceId, hidden)
      out.push({ spaceId: space.spaceId, tree: hidden })
    }
    return out
  }, [spaces, key])

  /**
   * Apply a change to whatever the url says RIGHT NOW.
   *
   * A function of the current tree rather than of the tree this render closed
   * over, because a drag fires many times between renders: two moves computed
   * from the same stale base means the first one is thrown away, which is felt
   * as a seam that lags the pointer and then jumps.
   *
   * `replace` decides whether the change is history. Rearranging panes is
   * navigation you may want to undo — a mis-drop, a pane closed by accident, is
   * a back button away — but a RESIZE is not: it fires per pointer-move, and
   * pushing an entry each time buries every real entry under a hundred
   * one-pixel ones.
   */
  const updateTree = useCallback(
    (
      change: (current: ContentNode) => ContentNode | null,
      opts?: { replace?: boolean },
    ) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          const current =
            decodeContent(params.get(CONTENT_PARAM)) ?? emptyTree()
          const encoded = encodeContent(change(current))
          if (encoded) params.set(CONTENT_PARAM, encoded)
          else params.delete(CONTENT_PARAM)
          return params
        },
        { replace: opts?.replace ?? false },
      )
    },
    [setSearchParams],
  )

  /**
   * Replace the tree with one computed from THIS render's tree.
   *
   * It records `next` as the tree to adopt ids from before handing it to the
   * url, and that line is what makes ids survive a change of SHAPE. The url
   * carries no ids, so the change comes back from `decodeContent` with fresh
   * ones, and `adoptIds` can only keep the old ones where the two trees line up
   * child for child — which they do not when a pane has just been added or
   * closed. Adopting from the pre-change tree therefore renamed every pane on
   * every split: the caret's pane no longer existed, so focus fell back to the
   * first pane, and the NEXT split landed there instead of where you were
   * working. (It also remounted every pane, taking scroll positions and iframes
   * with it.)
   *
   * Adopting from the tree we just computed lines them up exactly, because it
   * IS the tree the url is about to describe.
   */
  const setTree = useCallback(
    (next: ContentNode | null) => {
      if (next) remembered.current.set(key, next)
      else remembered.current.delete(key)
      updateTree(() => next)
    },
    [updateTree, key],
  )

  const [chrome, setChromeState] = useState<Chrome>(loadChrome)
  useEffect(() => {
    try {
      localStorage.setItem(CHROME_KEY, JSON.stringify(chrome))
    } catch {
      // best-effort persistence
    }
  }, [chrome])

  const [focusedId, setFocusedIdState] = useState<string | null>(null)
  /**
   * The synchronous focus, for two window commands handled in one renderer
   * tick. React state deliberately waits for the next render; `set_view`,
   * however, may open a pane and then arrange it before that render. Without
   * this ref, Focus keeps the pane that was focused before the open and throws
   * away the pane the tool just reported opening.
   */
  const focusedRef = useRef<string | null>(null)
  const setFocusedId = useCallback((id: string | null) => {
    focusedRef.current = id
    setFocusedIdState(id)
  }, [])
  // A pane that closed cannot keep focus, and a stale id would send the next open
  // to a pane that no longer exists.
  const focused = useMemo(
    () =>
      focusedId && findLeaf(tree, focusedId)
        ? focusedId
        : (leaves(tree)[0]?.id ?? null),
    [focusedId, tree],
  )

  /**
   * The maximized pane, per space (RFC 0305 §3a).
   *
   * Keyed by the space in the url rather than by leaf id alone: ids are adopted
   * across a space switch when two trees have the same shape, so a bare id would
   * maximize a pane in a space nobody maximized. Never persisted, so a reload
   * returns the full layout rather than half of one.
   */
  const [maximized, setMaximized] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  )
  // A leaf that left the tree, or a tree down to one pane, has nothing to hide.
  const maximizedId = useMemo(() => {
    const id = maximized.get(scope)
    return id && leaves(tree).length > 1 && findLeaf(tree, id) ? id : null
  }, [maximized, scope, tree])

  const restore = useCallback(
    () =>
      setMaximized((prev) => {
        if (!prev.has(scope)) return prev
        const next = new Map(prev)
        next.delete(scope)
        return next
      }),
    [scope],
  )

  /** ⌘⇧Enter: maximize the focused pane, or put the space back. */
  const toggleMaximized = useCallback(() => {
    if (maximizedId) return restore()
    if (!focused || leaves(tree).length < 2) return
    setMaximized((prev) => new Map(prev).set(scope, focused))
  }, [maximizedId, restore, focused, tree, scope])

  /** Give a newly opened destination the full content area immediately. */
  const maximize = useCallback(
    (id: string) => {
      const current = remembered.current.get(key) ?? tree
      if (leaves(current).length < 2 || !findLeaf(current, id)) return
      setMaximized((prev) => new Map(prev).set(scope, id))
    },
    [key, tree, scope],
  )

  /**
   * Bring something onto the screen. Four steps, in order:
   *
   *   1. FILL   — the pane you are in is EMPTY, so it becomes this.
   *   2. REVEAL — a pane already shows it, so go there.
   *   3. REUSE  — a document pane exists, so show it THERE.
   *   4. OPEN   — nothing to reuse, so open a pane beside the focused one.
   *
   * Step 3 is the one that was missing, and its absence was the whole "opens in
   * a smaller slice of the same screen" complaint: every open added a column, so
   * four documents meant four columns and an agent panel 180px wide, none of
   * which anybody asked for. Splitting beside is right for the FIRST document —
   * you want it next to the conversation — and wrong for the second, which is
   * the same move any editor makes: files replace each other in a group, and you
   * split when you say so.
   *
   * The pane reused is the focused one when that is a document, else the first
   * document pane, so the choice is predictable rather than "wherever it went
   * last time".
   *
   * A document never replaces an AGENT block: that would close a conversation to
   * open a file, and the conversation is not a thing you can re-open from the
   * sidebar in the state it was in.
   *
   * `again: true` opts out of all of it and splits, which is what a deliberate
   * second view of one document wants (and what dragging a pane to an edge does
   * through `move`).
   *
   * `beside: true` skips only REUSE: an agent's `set_view` says it opens a
   * document beside the focused pane, so a second document splits instead of
   * replacing the first. FILL and REVEAL still apply.
   *
   * RETURNS THE PANE IT SETTLED ON, which is not always a new one: the rules
   * above may fill the empty pane you were in, reveal a pane already showing
   * this, or reuse a document or terminal pane. A caller that needs to say
   * something about "the pane I just opened" cannot work that out from outside
   * — it would have to re-implement every rule here and would get it wrong the
   * first time one changed.
   */
  const place = useCallback(
    (
      content: Content,
      edge: DropEdge = 'right',
      opts?: { again?: boolean; beside?: boolean },
    ): string => {
      // A new pane takes the caret, the way a new split does in a terminal: the
      // thing you just asked for is the thing you are now in, and the split
      // AFTER this one goes there rather than back to wherever you were.
      const id = leafId()
      if (opts?.again) {
        setTree(openLeaf(tree, content, focused, edge, id))
        setFocusedId(id)
        return id
      }
      const here = focused ? leafAt(tree, focused) : null
      /**
       * AN EMPTY PANE TAKES WHAT YOU PICK NEXT.
       *
       * This is what makes ⌘D's pane a pane rather than a suggestion: it is
       * standing there asking what goes in it, so the next thing you pick goes
       * in it, whatever kind of thing that is.
       *
       * Before REVEAL, deliberately. Picking something already open elsewhere
       * would otherwise jump you to that pane and leave the empty one sitting
       * there — but you asked for this pane, and a second view of one document
       * is a thing people want. Nothing is lost either way: the pane you came
       * from is still on screen.
       */
      if (here?.content.kind === 'picker') {
        setTree(setLeafContent(tree, here.id, content))
        setFocusedId(here.id)
        return here.id
      }
      const shown = leafShowing(tree, content)
      if (shown) {
        setFocusedId(shown.id)
        return shown.id
      }
      /**
       * A CODE FILE JOINS THE CODE PANE AS A TAB, `beside` or not.
       *
       * Reviewing a branch means reading several files, and replacing left one
       * on screen while splitting spent a column on each. A tab is what an
       * editor does with the second file. `again` still splits.
       */
      const tab = openCodeTab(tree, content, focused)
      if (tab) {
        setTree(tab.tree)
        setFocusedId(tab.id)
        return tab.id
      }
      if (content.kind === 'document' && !opts?.beside) {
        // A pane holding several files is not given away to one document:
        // that would close every tab to show it.
        const reusable = (l: ContentLeaf) =>
          l.content.kind === 'document' && paneTabs(l.content).length < 2
        const reuse =
          here && reusable(here) ? here : (leaves(tree).find(reusable) ?? null)
        if (reuse) {
          setTree(setLeafContent(tree, reuse.id, content))
          setFocusedId(reuse.id)
          return reuse.id
        }
      }
      /**
       * A TERMINAL REUSES A TERMINAL PANE, and only a terminal pane.
       *
       * The same reasoning as documents — several shells should not mean a
       * column each — with the asymmetry that makes it matter: the rule above
       * looks only for DOCUMENT panes, so a document can never take the pane a
       * shell is in. That is not incidental (RFC 0276 §4). A pty is not
       * re-openable the way a document is; evicting one ends a process, and it
       * would be a running build that paid for opening a file.
       */
      if (content.kind === 'terminal') {
        const reuse =
          here?.content.kind === 'terminal'
            ? here
            : (leaves(tree).find((l) => l.content.kind === 'terminal') ?? null)
        if (reuse) {
          setTree(setLeafContent(tree, reuse.id, content))
          setFocusedId(reuse.id)
          return reuse.id
        }
      }
      setTree(openLeaf(tree, content, focused, edge, id))
      setFocusedId(id)
      return id
    },
    [tree, focused, setTree, setFocusedId],
  )

  // Anything that lands outside the maximized pane restores first (RFC 0305
  // §3c): a split, or a reveal of a hidden pane, is invisible otherwise.
  const open = useCallback(
    (
      content: Content,
      edge?: DropEdge,
      opts?: { again?: boolean; beside?: boolean },
    ): string => {
      const id = place(content, edge, opts)
      if (maximizedId && id !== maximizedId) restore()
      return id
    },
    [place, maximizedId, restore],
  )

  /**
   * Change what one pane shows: the back button, and picking from the list.
   *
   * `replace` for a pane following itself rather than being sent somewhere: a
   * web page reporting where it navigated to. That is the page's history, not
   * the window's, and pushing an entry per click would bury the window's back
   * button under every link followed inside the page.
   */
  const showIn = useCallback(
    (leafId: string, content: Content, opts?: { replace?: boolean }) => {
      const next = setLeafContent(tree, leafId, content)
      if (!opts?.replace) {
        setTree(next)
        return
      }
      if (next) remembered.current.set(key, next)
      updateTree(() => next, { replace: true })
    },
    [tree, setTree, updateTree, key],
  )

  const close = useCallback(
    (leafId: string) => {
      if (leafId === maximizedId) restore()
      // Closing the LAST pane leaves an empty pane, not the session list: a
      // null tree would be refilled with `emptyTree()`, which is a fresh
      // window's default, not what closing everything in a space asks for.
      setTree(closeLeaf(tree, leafId) ?? pickerLeaf())
    },
    [tree, setTree, maximizedId, restore],
  )

  /** Show one of a code pane's tabs. History, like any change of what is open. */
  const showTab = useCallback(
    (leafId: string, docId: string) => {
      const next = selectTab(tree, leafId, docId)
      if (next !== tree) setTree(next)
    },
    [tree, setTree],
  )

  /** Open a code file as a tab in one pane, from its Files column (RFC 0354). */
  const openTab = useCallback(
    (leafId: string, docId: string) => {
      const next = addTab(tree, leafId, docId)
      if (next !== tree) setTree(next)
    },
    [tree, setTree],
  )

  /** Close one tab; the last one closes its pane, the way `close` does. */
  const closeTabIn = useCallback(
    (leafId: string, docId: string) => {
      const next = closeTab(tree, leafId, docId)
      if (next === tree) return
      if (!findLeaf(next, leafId) && leafId === maximizedId) restore()
      setTree(next ?? pickerLeaf())
    },
    [tree, setTree, maximizedId, restore],
  )

  const move = useCallback(
    (leafId: string, targetId: string, edge: DropEdge) => {
      restore()
      setTree(moveLeaf(tree, leafId, targetId, edge))
    },
    [tree, setTree, restore],
  )

  const resize = useCallback(
    (path: NodePath, index: number, delta: number) =>
      updateTree((current) => resizeSplit(current, path, index, delta), {
        replace: true,
      }),
    [updateTree],
  )

  const applyArrangement = useCallback(
    (preset: ArrangePreset) => {
      restore()
      // `setTree` records a just-opened tree synchronously in `remembered`, so
      // read that before the render catches up. This makes open + layout one
      // honest operation instead of arranging the stale pre-open tree.
      const current = remembered.current.get(key) ?? tree
      setTree(arrange(current, preset.shape, focusedRef.current ?? focused))
    },
    [key, tree, focused, setTree, restore],
  )

  const setChrome = useCallback(
    (patch: Partial<Chrome>) => setChromeState((c) => ({ ...c, ...patch })),
    [],
  )
  const toggleSidebar = useCallback(
    () => setChromeState((c) => ({ ...c, sidebar: !c.sidebar })),
    [],
  )
  const toggleComposer = useCallback(
    () => setChromeState((c) => ({ ...c, composerHidden: !c.composerHidden })),
    [],
  )
  const toggleSettings = useCallback(
    () => setChromeState((c) => ({ ...c, settings: !c.settings })),
    [],
  )
  const toggleSpacesAsTabs = useCallback(
    () => setChromeState((c) => ({ ...c, spacesAsTabs: !c.spacesAsTabs })),
    [],
  )
  const setSidebarWidth = useCallback(
    (w: number) =>
      setChromeState((c) => ({
        ...c,
        sidebarWidth: clamp(w, SIDEBAR_MIN, SIDEBAR_MAX),
      })),
    [],
  )

  /**
   * Widen or narrow the sidebar BY a delta.
   *
   * Not `setSidebarWidth(width + dx)` from a caller, which is what this replaces:
   * that reads the width the caller's render captured, and a drag fires many
   * times per render, so every delta lands on the same stale base and the seam
   * ends up wherever the last event happened to say instead of where the pointer
   * is. The same fault the pane seams had.
   */
  const nudgeSidebarWidth = useCallback(
    (dx: number) =>
      setChromeState((c) => ({
        ...c,
        sidebarWidth: clamp(c.sidebarWidth + dx, SIDEBAR_MIN, SIDEBAR_MAX),
      })),
    [],
  )

  return {
    tree,
    hiddenTrees,
    chrome,
    focusedId: focused,
    setFocusedId,
    maximizedId,
    toggleMaximized,
    maximize,
    setTree,
    open,
    showIn,
    close,
    showTab,
    openTab,
    closeTab: closeTabIn,
    move,
    resize,
    applyArrangement,
    setChrome,
    toggleSidebar,
    toggleComposer,
    toggleSettings,
    toggleSpacesAsTabs,
    setSidebarWidth,
    nudgeSidebarWidth,
  }
}
